/**
 * Formatting Utilities for Logistiat
 * 
 * Ensures consistent formatting throughout the application:
 * - English numerals only (no Arabic numerals)
 * - Georgian date format (DD/MM/YYYY)
 * - SAR currency formatting
 * - Proper RTL/LTR handling
 */

/**
 * Format numbers to always use English numerals
 * Prevents Arabic numerals from appearing in the UI
 */
export const formatNumber = (number, options = {}) => {
  if (number === null || number === undefined || isNaN(number)) {
    return '0';
  }
  
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: options.decimals || 0,
    maximumFractionDigits: options.decimals || (number % 1 === 0 ? 0 : 2),
    ...options
  }).format(number);
  
  // Ensure English numerals by replacing any Arabic numerals
  return formatted.replace(/[٠-٩]/g, (match) => {
    const arabicNumerals = '٠١٢٣٤٥٦٧٨٩';
    const englishNumerals = '0123456789';
    return englishNumerals[arabicNumerals.indexOf(match)];
  });
};

/**
 * Format currency amounts in SAR with English numerals
 */
export const formatCurrency = (amount, options = {}) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return 'SAR 0';
  }
  
  const formattedNumber = formatNumber(amount, {
    minimumFractionDigits: 0,
    maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    ...options
  });
  
  return `SAR ${formattedNumber}`;
};

/**
 * Format dates in Georgian format (DD/MM/YYYY)
 * Always uses English numerals regardless of locale
 */
export const formatDate = (date, options = {}) => {
  if (!date) return '';
  
  let dateObj;
  if (date.toDate && typeof date.toDate === 'function') {
    // Firestore Timestamp
    dateObj = date.toDate();
  } else if (typeof date === 'string' || typeof date === 'number') {
    dateObj = new Date(date);
  } else if (date instanceof Date) {
    dateObj = date;
  } else {
    return '';
  }
  
  if (isNaN(dateObj.getTime())) {
    return '';
  }
  
  const day = dateObj.getDate().toString().padStart(2, '0');
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const year = dateObj.getFullYear().toString();
  
  if (options.format === 'input') {
    // For HTML date inputs (YYYY-MM-DD)
    return `${year}-${month}-${day}`;
  }
  
  // Default Georgian format (DD/MM/YYYY) with English numerals
  const formatted = `${day}/${month}/${year}`;
  
  // Ensure English numerals
  return formatted.replace(/[٠-٩]/g, (match) => {
    const arabicNumerals = '٠١٢٣٤٥٦٧٨٩';
    const englishNumerals = '0123456789';
    return englishNumerals[arabicNumerals.indexOf(match)];
  });
};

/**
 * Format date and time in Georgian format with English numerals
 */
export const formatDateTime = (date, options = {}) => {
  if (!date) return '';
  
  let dateObj;
  if (date.toDate && typeof date.toDate === 'function') {
    dateObj = date.toDate();
  } else if (typeof date === 'string' || typeof date === 'number') {
    dateObj = new Date(date);
  } else if (date instanceof Date) {
    dateObj = date;
  } else {
    return '';
  }
  
  if (isNaN(dateObj.getTime())) {
    return '';
  }
  
  const dateFormatted = formatDate(dateObj);
  const hours = dateObj.getHours().toString().padStart(2, '0');
  const minutes = dateObj.getMinutes().toString().padStart(2, '0');
  
  const timeFormatted = `${hours}:${minutes}`;
  
  // Ensure English numerals in time
  const timeWithEnglishNumerals = timeFormatted.replace(/[٠-٩]/g, (match) => {
    const arabicNumerals = '٠١٢٣٤٥٦٧٨٩';
    const englishNumerals = '0123456789';
    return englishNumerals[arabicNumerals.indexOf(match)];
  });
  
  return `${dateFormatted} ${timeWithEnglishNumerals}`;
};

/**
 * Format percentage with English numerals
 */
export const formatPercentage = (value, decimals = 1) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0%';
  }
  
  const formatted = formatNumber(value, { decimals });
  return `${formatted}%`;
};

/**
 * Parse date from DD/MM/YYYY format to Date object
 */
export const parseDate = (dateString) => {
  if (!dateString) return null;
  
  // Handle DD/MM/YYYY format
  const parts = dateString.split('/');
  if (parts.length === 3) {
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
    const year = parseInt(parts[2], 10);
    return new Date(year, month, day);
  }
  
  // Fallback to standard Date parsing
  return new Date(dateString);
};

/**
 * Ensure text contains only English numerals
 * Useful for input validation and display
 */
export const ensureEnglishNumerals = (text) => {
  if (!text) return text;
  
  return text.toString().replace(/[٠-٩]/g, (match) => {
    const arabicNumerals = '٠١٢٣٤٥٦٧٨٩';
    const englishNumerals = '0123456789';
    return englishNumerals[arabicNumerals.indexOf(match)];
  });
};

/**
 * Format invoice number with English numerals
 */
export const formatInvoiceNumber = (invoiceNumber) => {
  if (!invoiceNumber) return '';
  return ensureEnglishNumerals(invoiceNumber.toString());
};

/**
 * Format shipment tracking number with English numerals
 */
export const formatTrackingNumber = (trackingNumber) => {
  if (!trackingNumber) return '';
  return ensureEnglishNumerals(trackingNumber.toString());
};